import { useEffect, useState, useCallback } from 'react';
import AccountLayout from '@/components/feature/AccountLayout';
import { Select } from '@/components/common';
import { fetchSupportTickets } from '@/service/api/supportTicket';
import { CreateTicketModal, TicketDetailModal } from './modules';

// 工单类型映射
const TICKET_TYPE_MAP: Record<string, { label: string; bgClass: string; textClass: string }> = {
  ACCOUNT: { label: '账号问题', bgClass: 'bg-blue-50', textClass: 'text-blue-700' },
  VIDEO: { label: '视频制作', bgClass: 'bg-purple-50', textClass: 'text-purple-700' },
  PAYMENT: { label: '支付交易', bgClass: 'bg-amber-50', textClass: 'text-amber-700' },
  CONTENT: { label: '内容问题', bgClass: 'bg-emerald-50', textClass: 'text-emerald-700' },
  OTHER: { label: '其他咨询', bgClass: 'bg-slate-100', textClass: 'text-slate-700' },
};

// 期望联系时间映射
const PREFERRED_CALL_MAP: Record<string, string> = {
  ANY_TIME: '随时可联系',
  WORKDAY_09_18: '工作日 09:00 - 18:00',
  WORKDAY_09_22: '工作日 09:00 - 22:00',
  DAILY_09_22: '每天 09:00 - 22:00',
  NO_CALL: '无需电话联系',
};

// Select 选项配置
const TYPE_FILTER_OPTIONS = [
  { label: '全部类型', value: 'ALL' },
  { label: '账号问题', value: 'ACCOUNT' },
  { label: '视频制作', value: 'VIDEO' },
  { label: '支付交易', value: 'PAYMENT' },
  { label: '内容问题', value: 'CONTENT' },
  { label: '其他咨询', value: 'OTHER' },
];

const STATUS_FILTER_OPTIONS = [
  { label: '全部状态', value: 'ALL' },
  { label: '处理中', value: 'PENDING' },
  { label: '已处理', value: 'RESOLVED' },
];

// 工单状态渲染
function renderTicketStatus(status?: string) {
  const s = (status || '').toUpperCase();
  switch (s) {
    case 'PENDING':
    case 'OPEN':
    case 'SUBMITTED':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">处理中</span>;
    case 'RESOLVED':
    case 'COMPLETED':
    case 'CLOSED':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">已处理</span>;
    case 'PROCESSING':
    case 'IN_PROGRESS':
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">服务人员跟进中</span>;
    default:
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{status || '提交成功'}</span>;
  }
}

// 格式化日期
function formatDate(dateStr?: string) {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

export default function SupportTicketsPage() {
  const [tickets, setTickets] = useState<Api.SupportTicket.TicketResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // 筛选状态
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');

  // 新增工单 Modal 状态
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // 详情与沟通 Modal 状态
  const [selectedTicket, setSelectedTicket] = useState<Api.SupportTicket.TicketResponse | null>(null);

  // 加载工单列表
  const loadTickets = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: Api.SupportTicket.GetTicketListParams = {
        page: 1,
        page_size: 50,
      };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (typeFilter !== 'ALL') params.ticket_type = typeFilter;

      const res = await fetchSupportTickets(params);
      if (res && Array.isArray(res.items)) {
        setTickets(res.items);
      } else {
        setTickets([]);
      }
    } catch (err: any) {
      console.error('加载工单列表失败', err);
      setLoadError(err?.message || '加载工单列表失败');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  return (
    <AccountLayout
      activePath="/support-tickets"
      breadcrumbs={[{ label: '个人中心', href: '/profile' }, { label: '服务工单' }]}
    >
      <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 mb-6">
        {/* 头部标题与新建按钮 */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-emerald-600"></i>
              服务工单
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              如在平台使用中遇到问题或有任何需求建议，欢迎提交工单与我们联系。
            </p>
          </div>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-sm transition-all shadow-sm hover:shadow cursor-pointer flex-shrink-0"
          >
            <i className="ri-add-line text-lg"></i>
            提交新工单
          </button>
        </div>

        {/* 筛选栏 */}
        <div className="flex flex-wrap items-center justify-between gap-4 my-6 bg-slate-50 p-4 rounded-xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">类型:</span>
            <Select
              options={TYPE_FILTER_OPTIONS}
              value={typeFilter}
              onChange={(val) => setTypeFilter(String(val))}
              size="sm"
              className="w-36"
            />

            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider ml-2">状态:</span>
            <Select
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              onChange={(val) => setStatusFilter(String(val))}
              size="sm"
              className="w-32"
            />
          </div>

          <div className="text-xs text-slate-400">共 {tickets.length} 条记录</div>
        </div>

        {/* 列表主体 */}
        {loading ? (
          <div className="py-16 text-center text-slate-400 flex flex-col items-center justify-center">
            <i className="ri-loader-4-line text-3xl animate-spin text-emerald-600 mb-2"></i>
            <p className="text-sm">加载工单记录中...</p>
          </div>
        ) : loadError ? (
          <div className="py-12 text-center text-slate-500">
            <i className="ri-error-warning-line text-3xl text-rose-500 mb-2 block"></i>
            <p className="text-sm text-slate-600 mb-3">{loadError}</p>
            <button
              onClick={loadTickets}
              className="px-4 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium transition-colors"
            >
              重试
            </button>
          </div>
        ) : tickets.length === 0 ? (
          <div className="py-16 text-center text-slate-400">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-archive-line text-2xl text-slate-400"></i>
            </div>
            <p className="text-sm text-slate-600 font-medium mb-1">暂无相关工单</p>
            <p className="text-xs text-slate-400 mb-4">如果您在使用中有任何疑问，可点击上方的按钮提交工单</p>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-medium hover:bg-emerald-100 transition-colors"
            >
              <i className="ri-add-line"></i> 立即提交工单
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {tickets.map((item) => {
              const typeInfo = TICKET_TYPE_MAP[item.ticket_type || 'OTHER'] || TICKET_TYPE_MAP.OTHER;
              return (
                <div
                  key={item.id || item.ticket_no}
                  onClick={() => setSelectedTicket(item)}
                  className="p-4 sm:p-5 rounded-xl border border-slate-100 hover:border-emerald-200 bg-white hover:bg-emerald-50/20 transition-all cursor-pointer group shadow-sm hover:shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2.5 py-0.5 rounded-md font-medium ${typeInfo.bgClass} ${typeInfo.textClass}`}
                      >
                        {typeInfo.label}
                      </span>
                      <span className="text-xs font-mono text-slate-400">#{item.ticket_no || item.id}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {renderTicketStatus(item.status)}
                      <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-slate-800 line-clamp-2 mb-3 group-hover:text-emerald-800 transition-colors">
                    {item.description}
                  </p>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-50 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-phone-line"></i> {PREFERRED_CALL_MAP[item.preferred_call_at || 'ANY_TIME']}
                    </span>
                    <span className="text-emerald-600 font-medium flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                      查看对话与进度 <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 创建工单 Modal 弹窗 */}
      <CreateTicketModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={loadTickets}
      />

      {/* 工单详情与反馈 Modal 抽屉 */}
      <TicketDetailModal
        ticket={selectedTicket}
        onClose={() => setSelectedTicket(null)}
        renderTicketStatus={renderTicketStatus}
        formatDate={formatDate}
      />
    </AccountLayout>
  );
}

